import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Send, Phone, Search, ChevronDown, Filter } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { reloadTextingNumbers, sendTextMessage, fromReduxSafeRegisteredTwilioNumber, toReduxSafeRegisteredTwilioNumber } from 'lib/redux/sessions/texting/slice';
import { SessionListResponse } from 'interfaces/sessions';
import { SessionV2Columns } from 'interfaces/table';
import { ConversationType, RegisteredTwilioNumber } from 'interfaces/db';
import { cleanNumber, formatNumber, getClosestGeographicNumber } from './number-helpers';
import { useUserData } from '../../lib/redux/users/user-data-hook';

// Thread pair type definition
interface TextThreadPair {
  repNumber: string;
  prospectNumber: string;
  label: string;
  count: number;
}

interface TextComposerProps {
    phoneNumbers: string[]
    items: SessionListResponse[]
    isLoading: boolean
    activeTextThreadPair: TextThreadPair | null
    onThreadSelected: (thread: TextThreadPair | null) => void
}

function formatPhoneNumber(number: string): string {
  // Remove any non-digit characters
  const cleaned = number.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX if it's a 10-digit number
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Format as +X (XXX) XXX-XXXX if it's 11 digits starting with 1
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  // Return as is if it doesn't match known formats
  return number;
}

function DisabledTextComposer() {
    return <div className="border-t border-gray-200 bg-white p-4">
        <div className="flex gap-3">
            <div className="flex-1 flex items-center gap-2">
                <input type="text" placeholder="Write a message..." className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" disabled />
            </div>
        </div>
    </div>
}

function NoNumbersTextComposer() {
    return <div className="border-t border-gray-200 bg-white p-4">
        <div className="flex gap-3">
            <div className="flex-1 flex items-center gap-2">
                <input type="text" placeholder="Write a message..." className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" disabled />
            </div>
        </div>
    </div>
}

function getClosestNumber(
    targetNumber: string | null,
    textingNumbers: RegisteredTwilioNumber[]
): RegisteredTwilioNumber | null {
    if (!targetNumber) return null;
    const closest = getClosestGeographicNumber(cleanNumber(targetNumber), textingNumbers.map(n => n.number));
    if (!closest) return null;
    return textingNumbers.find(n => n.number === closest) || null;
}

function ThreadSelector({
    textThreadPairs,
    activeTextThreadPair,
    onThreadSelected,
}: {
    textThreadPairs: TextThreadPair[];
    activeTextThreadPair: TextThreadPair | null;
    onThreadSelected: (pair: TextThreadPair | null) => void;
}) {
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (textThreadPairs.length <= 1) return null;

    // Calculate total thread count
    const totalThreadCount = textThreadPairs.length;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700 hover:bg-blue-100 transition-colors h-8"
                onClick={() => setShowDropdown(!showDropdown)}
            >
                <span>{activeTextThreadPair?.label || `All Threads (${totalThreadCount})`}</span>
                <Filter size={14} />
            </button>
            
            {showDropdown && (
                <div className="absolute right-0 bottom-full mb-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
                    {/* All Threads option */}
                    <button
                        className={`w-full text-left px-4 py-2 text-sm ${
                            !activeTextThreadPair
                                ? 'bg-blue-50 font-medium text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => {
                            onThreadSelected(null);
                            setShowDropdown(false);
                        }}
                    >
                        <div className="flex justify-between items-center">
                            <span>All Threads</span>
                            <span className="bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
                                {totalThreadCount}
                            </span>
                        </div>
                    </button>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-100"></div>
                    
                    {/* Individual thread options */}
                    {textThreadPairs.map((pair) => (
                        <button
                            key={`${pair.repNumber}-${pair.prospectNumber}`}
                            className={`w-full text-left px-4 py-2 text-sm ${
                            activeTextThreadPair?.repNumber === pair.repNumber && 
                            activeTextThreadPair?.prospectNumber === pair.prospectNumber
                                ? 'bg-blue-50 font-medium text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => {
                                onThreadSelected(pair);
                                setShowDropdown(false);
                            }}
                        >
                            <div className="flex justify-between items-center">
                                <span>{pair.label}</span>
                                <span className="bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
                                    {pair.count}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function FromNumberSelector({ 
    selectedNumber,
    lastUsedNumber,
    closestNumber,
    numberOptions,
    onNumberChange,
    className = ""
}: {
    selectedNumber: RegisteredTwilioNumber | null,
    lastUsedNumber: RegisteredTwilioNumber | null,
    closestNumber: RegisteredTwilioNumber | null,
    numberOptions: RegisteredTwilioNumber[],
    onNumberChange: (number: RegisteredTwilioNumber | null) => void,
    className?: string
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { userRaw: user, allVisibleUsers: users } = useUserData();
    
    // Sort options with lastUsed and closest first
    const sortedOptions = useMemo(() => {
        const matching_user = (number: RegisteredTwilioNumber) => users?.find((v) => v.user_id === number.user_id)
        
        return numberOptions
            .map(number => ({
                value: number.number,
                formattedValue: formatNumber(number.number),
                number,
                isLastUsed: number.number === lastUsedNumber?.number,
                isClosest: number.number === closestNumber?.number,
                userName: matching_user(number)?.user_name,
                user_id: matching_user(number)?.user_id
            }))
            .sort((a, b) => {
                if (a.isLastUsed) return -1;
                if (b.isLastUsed) return 1;
                if (a.user_id === user?.user_id) return -1; // Current user first
                if (a.isClosest) return -1;
                if (b.isClosest) return 1;
                return 0;
            });
    }, [numberOptions, lastUsedNumber, closestNumber, user, users]);
    
    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return sortedOptions;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return sortedOptions.filter(option => 
            option.formattedValue.toLowerCase().includes(lowerSearchTerm) ||
            (option.userName && option.userName.toLowerCase().includes(lowerSearchTerm))
        );
    }, [sortedOptions, searchTerm]);
    
    const selectedOption = useMemo(() => {
        const selectedValue = selectedNumber?.number ||
                             lastUsedNumber?.number ||
                             closestNumber?.number;
        return sortedOptions.find(opt => opt.value === selectedValue) || sortedOptions[0];
    }, [selectedNumber, lastUsedNumber, closestNumber, sortedOptions]);
    
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 10);
        }
    }, [isOpen]);
    
    const handleOptionClick = useCallback((option: typeof sortedOptions[0]) => {
        onNumberChange(option.number);
        setIsOpen(false);
        setSearchTerm('');
    }, [onNumberChange]);
    
    return (
        <div className={`relative w-[240px] ${className}`} ref={ref}>
            {/* Selected value display */}
            <div 
                className="flex items-center px-2 h-8 bg-white border border-gray-300 rounded-md text-sm focus:outline-none hover:border-gray-400 cursor-pointer"
                onClick={() => setIsOpen(!isOpen)}
            >
                <Phone className="w-4 h-4 text-gray-400 mr-2" />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="truncate">
                        {selectedOption ? (
                            <>
                                {selectedOption.formattedValue}
                                {selectedOption.userName && ` - ${selectedOption.userName}`}
                            </>
                        ) : "Select number..."}
                    </span>
                    {selectedOption?.isLastUsed && (
                        <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded-full whitespace-nowrap border border-green-200">
                            Last Used
                        </span>
                    )}
                    {!selectedOption?.isLastUsed && selectedOption?.isClosest && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full whitespace-nowrap">
                            Closest
                        </span>
                    )}
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
            </div>
            
            {/* Dropdown menu */}
            {isOpen && (
                <div className="absolute bottom-full mb-1 left-0 w-full bg-white rounded-md shadow-lg border border-gray-200 z-10 max-h-[300px] flex flex-col overflow-hidden">
                    {/* Option list */}
                    <div className="overflow-y-auto flex-1">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    className={`
                                        px-3 py-2 cursor-pointer text-sm border-b border-gray-100 hover:bg-gray-50
                                        ${option.value === selectedOption?.value ? 'bg-blue-50 font-medium' : ''}
                                        ${option.isLastUsed ? 'bg-green-50' : option.isClosest ? 'bg-blue-50' : ''}
                                    `}
                                    onClick={() => handleOptionClick(option)}
                                >
                                    <div className="flex justify-between items-center">
                                        <span>
                                            {option.formattedValue}
                                            {option.userName && ` - ${option.userName}`}
                                        </span>
                                        {option.isLastUsed && (
                                            <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full border border-green-200">Last Used</span>
                                        )}
                                        {!option.isLastUsed && option.isClosest && (
                                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">Closest</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-3 text-sm text-gray-500 text-center">No matching numbers</div>
                        )}
                    </div>
                    
                    {/* Search input at bottom */}
                    <div className="p-2 border-t border-gray-200 bg-gray-50 sticky bottom-0">
                        <div className="relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Search by number or name..."
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ToNumberSelector({
    selectedRecipient,
    phoneNumbers,
    onRecipientChange,
    className = ""
}: {
    selectedRecipient: string | null,
    phoneNumbers: string[],
    onRecipientChange: (recipient: string) => void,
    className?: string
}) {
    return (
        <select
            value={selectedRecipient || ''}
            onChange={(e) => onRecipientChange(e.target.value)}
            className={`w-[140px] px-2 h-8 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${className}`}
        >
            {phoneNumbers.map((number) => (
                <option key={number} value={number}>
                    {formatPhoneNumber(number)}
                </option>
            ))}
        </select>
    );
}

// Helper functions for TextComposer
function shouldShowThreadSelector(textThreadPairs: TextThreadPair[]): boolean {
    // Rule 4: Only show thread selector if there is more than one thread
    return textThreadPairs.length > 1;
}

function shouldShowTargetNumberSelector(
    activeTextThreadPair: TextThreadPair | null,
    phoneNumbers: string[]
): boolean {
    // Rule 2: Show target selector if no thread selected AND multiple numbers
    // Rule 3: Don't show if only one number
    return !activeTextThreadPair && phoneNumbers.length > 1;
}

function shouldShowFromNumberSelector(
    activeTextThreadPair: TextThreadPair | null,
): boolean {
    // Rule 1: Don't show if thread is selected
    return !activeTextThreadPair;
}


function filterThreadPairs(
    textThreadPairs: TextThreadPair[],
    selectedPhoneNumbers: string[]
): TextThreadPair[] {
    // Rule 5: Filter thread pairs to only include selected numbers
    if (!selectedPhoneNumbers.length) return textThreadPairs;
    return textThreadPairs.filter(pair => 
        selectedPhoneNumbers.some(number => 
            cleanNumber(number) === cleanNumber(pair.prospectNumber)
        )
    );
}

function getLatestNumber(
    activeTextThreadPair: TextThreadPair | null,
    textingNumbers: RegisteredTwilioNumber[],
    items: SessionListResponse[],
    targetNumber: string | null
): RegisteredTwilioNumber | null {
    // If there's an active thread, use its rep number if available in texting numbers
    if (activeTextThreadPair) {
        const matchingNumber = textingNumbers.find(
            n => cleanNumber(n.number) === cleanNumber(activeTextThreadPair.repNumber)
        );
        if (matchingNumber) return matchingNumber;
    }

    // Create a map of cleaned phone numbers to their original RegisteredTwilioNumber
    const availableNumbers = new Map<string, RegisteredTwilioNumber>();
    textingNumbers.forEach(num => {
        availableNumbers.set(cleanNumber(num.number), num);
    });

    // Find the most recent session that used one of our available numbers with the target number
    for (const item of items) {
        const repNumber = item.result.get(SessionV2Columns.AGENT_ADDRESS) as string;
        const customerNumber = item.result.get(SessionV2Columns.CUSTOMER_ADDRESS) as string;
        if (!repNumber || !customerNumber) continue;

        // Only consider sessions with the current target number
        if (targetNumber && cleanNumber(customerNumber) !== cleanNumber(targetNumber)) continue;

        const cleanedRepNumber = cleanNumber(repNumber);
        const matchingNumber = availableNumbers.get(cleanedRepNumber);
        if (matchingNumber) {
            return matchingNumber;
        }
    }

    return null;
}

function getSortedTextingNumbers(
    textingNumbers: RegisteredTwilioNumber[],
    lastUsedNumber: RegisteredTwilioNumber | null,
    closestNumber: RegisteredTwilioNumber | null
): RegisteredTwilioNumber[] {
    return [...textingNumbers].sort((a, b) => {
        if (a.number === lastUsedNumber?.number) return -1;
        if (b.number === lastUsedNumber?.number) return 1;
        if (a.number === closestNumber?.number) return -1;
        if (b.number === closestNumber?.number) return 1;
        return 0;
    });
}

function getSelectedTargetNumber(
    activeTextThreadPair: TextThreadPair | null,
    selectedRecipient: string | null,
    phoneNumbers: string[]
): string | null {
    // 1. If thread is selected, use thread's prospect number
    if (activeTextThreadPair) {
        return activeTextThreadPair.prospectNumber;
    }

    // 2. If recipient is explicitly selected, use that
    if (selectedRecipient) {
        return selectedRecipient;
    }

    // 3. Default to first available phone number
    return phoneNumbers.length > 0 ? phoneNumbers[0] : null;
}

function getSelectedFromNumber(
    activeTextThreadPair: TextThreadPair | null,
    selectedNumber: RegisteredTwilioNumber | null,
    lastUsedNumber: RegisteredTwilioNumber | null,
    closestNumber: RegisteredTwilioNumber | null,
    textingNumbers: RegisteredTwilioNumber[]
): RegisteredTwilioNumber | null {
    // 1. If thread is selected, try to use thread's rep number
    if (activeTextThreadPair) {
        const matchingNumber = textingNumbers.find(
            n => cleanNumber(n.number) === cleanNumber(activeTextThreadPair.repNumber)
        );
        if (matchingNumber) return matchingNumber;
    }

    // 2. If number is explicitly selected, use that
    if (selectedNumber) {
        return selectedNumber;
    }

    // 3. Default priority: last used -> closest -> first available
    return lastUsedNumber || closestNumber || (textingNumbers.length > 0 ? textingNumbers[0] : null);
}

export function TextComposer(props: TextComposerProps) {
    const { phoneNumbers, items, isLoading, onThreadSelected, activeTextThreadPair } = props;
    const textingNumbersReduxSafe = useSelector((state: RootState) => state.textingNumbers.textingNumbers);
    const textingNumbers = useMemo(() => {
        if (!textingNumbersReduxSafe) return [];
        return textingNumbersReduxSafe.map(fromReduxSafeRegisteredTwilioNumber);
    }, [textingNumbersReduxSafe]);
    const textingNumbersLoaded = useSelector((state: RootState) => state.textingNumbers.textingNumbersLoaded);
    const dispatch = useDispatch();
    const [message, setMessage] = useState('');
    const [selectedNumber, setSelectedNumber] = useState<RegisteredTwilioNumber | null>(null);
    const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);

    // Load texting numbers

    // Reset message when thread changes

    // Set initial recipient when phone numbers change
    useEffect(() => {
        setSelectedRecipient(null)
        setSelectedNumber(null)
    }, [phoneNumbers]);

    // Auto-update closest number when target recipient changes
    const closestNumber = useMemo(() => {
        const targetNumber = activeTextThreadPair?.prospectNumber || selectedRecipient;
        return getClosestNumber(targetNumber, textingNumbers);
    }, [activeTextThreadPair?.prospectNumber, selectedRecipient, textingNumbers]);

    // Get the last used number
    const lastUsedNumber = useMemo(() => {
        return getLatestNumber(activeTextThreadPair, textingNumbers, items, selectedRecipient);
    }, [activeTextThreadPair, textingNumbers, items, selectedRecipient]);

    // Auto-select last used or closest number if no number is selected
    useEffect(() => {
        if (!selectedNumber) {
            if (lastUsedNumber) {
                setSelectedNumber(lastUsedNumber);
            } else if (closestNumber) {
                setSelectedNumber(closestNumber);
            }
        }
    }, [lastUsedNumber, closestNumber, selectedNumber]);

    // Memoize all computed values in a single useMemo to prevent hook count changes
    const {
        textThreadPairs,
        sortedTextingNumbers,
        showThreadSelector,
        showTargetNumberSelector,
        showFromNumberSelector
    } = useMemo(() => {
        // Get all text conversation items
        const textItems = items.filter(item => 
            item.result.get(SessionV2Columns.CONVERSATION_TYPE) === ConversationType.MESSAGING
        );

        // Identify unique text thread pairs
        const pairs = new Map<string, TextThreadPair>();
        textItems.forEach(item => {
            const repNumber = item.result.get(SessionV2Columns.AGENT_ADDRESS) as string;
            const prospectNumber = item.result.get(SessionV2Columns.CUSTOMER_ADDRESS) as string;
            if (repNumber && prospectNumber) {
                const pairKey = `${cleanNumber(repNumber)}-${cleanNumber(prospectNumber)}`;
                if (pairs.has(pairKey)) {
                    const existing = pairs.get(pairKey)!;
                    pairs.set(pairKey, { ...existing, count: existing.count + 1 });
                } else {
                    pairs.set(pairKey, {
                        repNumber,
                        prospectNumber,
                        label: `${formatNumber(repNumber)} ↔ ${formatNumber(prospectNumber)}`,
                        count: 1
                    });
                }
            }
        });
        const textThreadPairs = Array.from(pairs.values());
        
        // Filter thread pairs based on selected phone numbers
        const filteredThreadPairs = filterThreadPairs(textThreadPairs, phoneNumbers);

        // Sort texting numbers with priority
        const sortedTextingNumbers = getSortedTextingNumbers(textingNumbers, lastUsedNumber, closestNumber);

        // Compute visibility flags based on rules
        const showThreadSelector = shouldShowThreadSelector(filteredThreadPairs);
        const showTargetNumberSelector = shouldShowTargetNumberSelector(activeTextThreadPair, phoneNumbers);
        const showFromNumberSelector = shouldShowFromNumberSelector(activeTextThreadPair);

        return {
            textThreadPairs: filteredThreadPairs,
            sortedTextingNumbers,
            showThreadSelector,
            showTargetNumberSelector,
            showFromNumberSelector
        };
    }, [items, activeTextThreadPair, textingNumbers, phoneNumbers, lastUsedNumber, closestNumber]);

    // Handle recipient change
    const handleRecipientChange = useCallback((recipient: string) => {
        setSelectedRecipient(recipient);
        // Clear selected number to allow auto-selection of closest number
        setSelectedNumber(null);
    }, []);


    const canSend = useMemo(() => {
        return message.trim() && getSelectedTargetNumber(activeTextThreadPair, selectedRecipient, phoneNumbers) && getSelectedFromNumber(activeTextThreadPair, selectedNumber, lastUsedNumber, closestNumber, textingNumbers);
    }, [message, activeTextThreadPair, selectedRecipient, phoneNumbers, selectedNumber, lastUsedNumber, closestNumber, textingNumbers]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canSend) return;

        const targetNumber = getSelectedTargetNumber(activeTextThreadPair, selectedRecipient, phoneNumbers);
        const fromNumber = getSelectedFromNumber(activeTextThreadPair, selectedNumber, lastUsedNumber, closestNumber, textingNumbers);

        // Only send if we have all required values and message
        if (message.trim() && fromNumber && targetNumber) {
            sendTextMessage({
                dispatch,
                from: toReduxSafeRegisteredTwilioNumber(fromNumber),
                to: targetNumber,
                message: message.trim()
            });
            setMessage('');
        }
    };

    useEffect(() => {
        if (!textingNumbersLoaded) reloadTextingNumbers(dispatch)
    }, [textingNumbersLoaded, dispatch])


    if (!textingNumbersLoaded) return <DisabledTextComposer />;
    if (textingNumbers.length === 0) return <NoNumbersTextComposer />;
    if (items.length === 0 && isLoading) return <DisabledTextComposer />;

    return (
        <div className="border-t border-gray-200 bg-white p-4">
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                {/* Selectors row - includes thread selector and number selectors */}
                {(showFromNumberSelector || showTargetNumberSelector || showThreadSelector) && (
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1">
                            {showFromNumberSelector && (
                                <FromNumberSelector
                                    key={`${lastUsedNumber?.number || ''}-${closestNumber?.number || ''}`}
                                    selectedNumber={selectedNumber}
                                    lastUsedNumber={lastUsedNumber}
                                    closestNumber={closestNumber}
                                    numberOptions={sortedTextingNumbers}
                                    onNumberChange={setSelectedNumber}
                                />
                            )}
                            {showTargetNumberSelector && (
                                <ToNumberSelector
                                    selectedRecipient={selectedRecipient}
                                    phoneNumbers={phoneNumbers}
                                    onRecipientChange={handleRecipientChange}
                                />
                            )}
                        </div>
                        {showThreadSelector && (
                            <ThreadSelector 
                                textThreadPairs={textThreadPairs}
                                activeTextThreadPair={activeTextThreadPair}
                                onThreadSelected={onThreadSelected}
                            />
                        )}
                    </div>
                )}

                {/* Message input and send button row */}
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Write a message..."
                        className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                        type="submit"
                        disabled={!canSend}
                        className="p-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
            </form>
        </div>
    );
}
